import { useMemo, useState } from "react";
import type { LoadFilterOptions } from "@generated/import-openapi.gen";

import {
  Button,
  ButtonStyle,
  DateTimePicker,
  Dropdown,
  Modal,
  RadioButtons,
  type RadioOption,
  TextArea,
} from "@prequel/shared-components";

import {
  buildLoadFilter,
  getLoadFilterError,
  isLoadFilterComplete,
  LOAD_FILTER_LABELS,
  LOAD_WINDOW_CLOCK_LABELS,
  type LoadFilterKind,
  type LoadWindowClock,
  MAX_CUSTOM_LOAD_RECORDS,
  parseRecordIDs,
} from "@/pages/import/StreamsPage/helpers";

const CLOCK_ITEMS = (Object.keys(LOAD_WINDOW_CLOCK_LABELS) as LoadWindowClock[]).map((key) => ({
  key,
  text: LOAD_WINDOW_CLOCK_LABELS[key],
}));

const HINT_CLASS = "text-xs font-medium text-stone-400";
const ERROR_CLASS = "text-xs font-medium text-red-600";

type RunLoadModalProps = {
  isSubmitting: boolean;
  initialRecordIDs?: string[];
  onCancel: () => void;
  onSubmit: (filter: LoadFilterOptions) => void;
};

const RunLoadModal = ({ isSubmitting, initialRecordIDs, onCancel, onSubmit }: RunLoadModalProps) => {
  const [kind, setKind] = useState<LoadFilterKind>(initialRecordIDs?.length ? "records" : "time_window");
  const [clock, setClock] = useState<LoadWindowClock>("last_modified");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [recordIDs, setRecordIDs] = useState(initialRecordIDs?.join(", ") ?? "");

  const parsedRecordIDs = useMemo(() => parseRecordIDs(recordIDs), [recordIDs]);
  const draft = { kind, clock, start, end, recordIDs: parsedRecordIDs };
  const error = getLoadFilterError(draft);
  const selectedClock = CLOCK_ITEMS.find((item) => item.key === clock);

  const options: RadioOption[] = [
    {
      key: "time_window",
      name: LOAD_FILTER_LABELS.time_window,
      content: (
        <div className="space-y-3">
          <Dropdown
            label="Based on"
            items={CLOCK_ITEMS}
            selectedItem={selectedClock}
            setSelectedItem={(item) => setClock(item.key)}
          />
          <DateTimePicker label="Start from" dateTime={start} setDateTime={setStart} />
          <DateTimePicker label="End at" dateTime={end} setDateTime={setEnd} />
          <p className={error ? ERROR_CLASS : HINT_CLASS}>
            {error ?? "Leave either bound empty to load without that bound."}
          </p>
        </div>
      ),
    },
    {
      key: "records",
      name: LOAD_FILTER_LABELS.records,
      content: (
        <div className="space-y-2">
          <TextArea
            id="record_ids_modal"
            label="Record IDs"
            placeholder="rec_001, rec_002, rec_003"
            value={recordIDs}
            onChangeHandler={setRecordIDs}
            invalid={error !== undefined}
            subtext="Whitespace or comma separated, or paste a JSON array."
          />
          <p className={error ? ERROR_CLASS : HINT_CLASS}>
            {error ?? `${parsedRecordIDs.length} of ${MAX_CUSTOM_LOAD_RECORDS} records`}
          </p>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="text-left">
        <Modal.Title as="h3" className="text-lg/6 font-medium text-stone-900">
          Run load
        </Modal.Title>
        <p className="mt-1 text-sm text-stone-500">Load only the records that match this filter.</p>
      </div>
      <div className="h-px w-full bg-stone-200" />

      <RadioButtons
        options={options}
        selectedOption={options.find((option) => option.key === kind)}
        setSelectedOption={(option: RadioOption) => setKind(option.key as LoadFilterKind)}
      />

      <div className="h-px w-full bg-stone-200" />
      <div className="space-x-3">
        <Button
          text="Queue Load"
          type={ButtonStyle.SECONDARY}
          disabled={!isLoadFilterComplete(draft) || error !== undefined || isSubmitting}
          onClick={() => onSubmit(buildLoadFilter(draft))}
        />
        <Button text="Cancel" type={ButtonStyle.TERTIARY} onClick={onCancel} />
      </div>
    </div>
  );
};

export default RunLoadModal;
